import React, { useContext, useEffect, Fragment, useState } from 'react';
import * as AiIcons from 'react-icons/ai';
import {
  add,
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  isAfter,
  isEqual,
  isPast,
  isSameDay,
  isToday,
  parse,
  parseISO,
  startOfMonth,
  startOfToday,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';

import { IReserva } from '../../types/Reserva';
import api from '../../services/api';
import Context, { IContext } from '../../context/Context';
import CustomAlert from '../CustomAlert';
import { IEspaco } from '../../types/Espaco';
import { formatCurrency } from '../../utils/formatCurrency';
import { IDatEsp } from '../../types/DatEsp';
import { tratarData } from '../../utils/tratarData';
import { ICheckout } from '../../types/Checkout';
import { ITipRes } from '../../types/TipRes';
import ResumoReserva from '../NovaReserva/ResumoReserva';

interface IAgendaReservaProps {
  espaco: IEspaco;
  tipoDeReserva: ITipRes;
  detalhesReserva?: {
    codusu: number;
    obs?: string;
    usu?: string;
  }
  onVoltar?: () => void;
}

function classNames(...classes: any[]) {
  return classes.filter(Boolean).join(' ')
}

export default function AgendaReserva({ espaco, tipoDeReserva, detalhesReserva, onVoltar }: IAgendaReservaProps) {
  const navigate = useNavigate();
  const { caminho } = useParams();
  const { usuario, setIsLoadingOverlay }: IContext = useContext(Context);

  const hoje = startOfToday();

  const [diaSelecionado, setDiaSelecionado] = useState<Date | undefined>();
  const [mesAtual, setMesAtual] = useState(format(hoje, 'MMM-yyyy'));
  const [reservas, setReservas] = useState<IReserva[]>([]);
  const [datasEspeciais, setDatasEspeciais] = useState<IDatEsp[]>([]);

  const dataLimiteParaReserva = new Date(new Date().setDate(new Date().getDate() + (espaco.tipesp.prares === 0 ? 999 : espaco.tipesp.prares)));

  const reservasFiltradas = reservas.filter(reserva => reserva.espaco.cod === espaco.cod);

  const primeiroDiaDoMesAtual = parse(mesAtual, 'MMM-yyyy', new Date());

  const dias = eachDayOfInterval({
    start: primeiroDiaDoMesAtual,
    end: endOfMonth(primeiroDiaDoMesAtual),
  })

  function mesAnterior() {
    const primeiroDiaDoMesAnterior = add(primeiroDiaDoMesAtual, { months: -1 });
    setMesAtual(format(primeiroDiaDoMesAnterior, 'MMM-yyyy'));
  }

  function proximoMes() {
    const primeiroDiaDoProximoMes = add(primeiroDiaDoMesAtual, { months: 1 })
    setMesAtual(format(primeiroDiaDoProximoMes, 'MMM-yyyy'))
  }

  function formataData(data: string) {
    const parsedDate = parse(data, 'dd/MM/yyyy', new Date());
    const dataFormatada = format(parsedDate, "yyyy-MM-dd'T'HH:mm");

    return dataFormatada;
  }

  async function getData() {
    try {
      setIsLoadingOverlay(true);

      // Disparar ambas as chamadas de API simultaneamente
      const [reservasResponse, datasEspeciaisResponse] = await Promise.all([
        api.get('/reservas', {
          params: {
            codesp: espaco.cod,
            sta: 'PAGO'
          }
        }),
        api.get('/datasEspeciais', {
          params: {
            codtipres: tipoDeReserva.cod
          }
        })
      ]);

      // Processar as respostas se todas forem bem-sucedidas
      if (reservasResponse.status === 200) {
        setReservas(reservasResponse.data);
      }

      if (datasEspeciaisResponse.status === 200) {
        setDatasEspeciais(datasEspeciaisResponse.data);
      }
    } catch (error: any) {
      if (error.response?.data?.erro) {
        toast.error(error.response.data.erro);
      } else {
        toast.error('Erro ao buscar dados');
      }
    } finally {
      setTimeout(() => {
        setIsLoadingOverlay(false);
      }, 500);
    }
  }

  async function postCheckout(datRes: Date) {
    try {
      setIsLoadingOverlay(true);

      //confirmar se espaço já foi reservado
      const response = await api.get('/reservas', {
        params: {
          codesp: espaco.cod,
          datres: datRes.toISOString().substring(0, 10),
          sta: 'PAGO'
        }
      });

      if (response.data.length > 0) {
        toast.error('Está data não está mais disponível para reserva');
        getData();
        return
      }

      if (!usuario?.cod) return toast.error('Erro ao criar reserva, faça login novamente.');

      const valorCalculado = valoresReserva(datRes);

      if (!valorCalculado || valorCalculado <= 0) {
        toast.error('Data indisponível para reserva.');
        getData();
        return;
      }

      const payloadReserva = {
        codesp: espaco.cod,
        datres: datRes.toISOString().substring(0, 10),
        codusu: detalhesReserva?.codusu ? detalhesReserva.codusu : usuario.cod,
        obs: detalhesReserva?.obs ? detalhesReserva.obs : '',
        val: valorCalculado,
        codtipres: tipoDeReserva.cod,
        ...(detalhesReserva?.codusu && { sta: 'PAGO' }),
      }

      const responseReserva = await api.post('/reservas', payloadReserva);

      if (!responseReserva.data?.cod) return toast.error('Erro ao criar reserva');

      if (detalhesReserva?.codusu) {
        toast.success('Reserva concluída');
        navigate(`/${caminho}/usuario/agenda`);
        return;
      }

      const payloadCheckout: ICheckout = {
        cod: responseReserva.data.cod,
        cliente: {
          raz: usuario?.usu || 'Associado',
          tipo: 'PF',
          cod: usuario?.cod.toString() || '',
          cgc: usuario?.cgc || '',
          tipocgc: 'CPF'
        },
        produtos: [
          {
            valor: responseReserva.data.val,
            produto: 'Reserva ' + espaco.esp + ' ' + format(datRes, 'dd/MM/yyyy', { locale: ptBR }),
            qua: 1
          }
        ]
      }

      const responseCheckout = await api.post('/checkout', payloadCheckout);

      if (responseCheckout.status === 200) {
        window.location.href = responseCheckout.data.checkoutUrl;
      }
    } catch (error: any) {
      if (error.response?.data?.erro) return toast.error(error.response.data.erro);
      toast.error('Erro ao concluir reserva');
    } finally {
      setTimeout(() => {
        setIsLoadingOverlay(false);
      }, 500);
    }
  }

  function valoresReserva(dia: Date): number {
    const diaDaSemana = getDay(dia);

    const dataEspecial = datasEspeciais.find((dataEspecial) => tratarData(dia.toISOString()) === dataEspecial.dat);
    if (dataEspecial) return dataEspecial.val;

    switch (diaDaSemana) {
      case 0:
        return tipoDeReserva.valdom;
      case 1:
        return tipoDeReserva.valseg;
      case 2:
        return tipoDeReserva.valter;
      case 3:
        return tipoDeReserva.valqua;
      case 4:
        return tipoDeReserva.valqui;
      case 5:
        return tipoDeReserva.valsex;
      case 6:
        return tipoDeReserva.valsab;
      default:
        return 0;
    }
  }

  function statusDoDia(dia: Date): StatusDia {
    if (isPast(dia) && !isToday(dia)) return 'passado';
    if (reservasFiltradas.some(reserva => isSameDay(parseISO(formataData(reserva.datres)), dia))) return 'reservado';
    if (dia >= dataLimiteParaReserva) return 'foraDoPrazo';
    if (
      datasEspeciais.find((dataEspecial) => tratarData(dia.toISOString()) === dataEspecial.dat)?.val === 0 ||
      valoresReserva(dia) === 0
    ) return 'indisponivel';

    return 'disponivel';
  }

  function handleDiaButtonClick(dia: Date) {
    if (statusDoDia(dia) !== 'disponivel') return;

    setDiaSelecionado(dia);
  }

  function confirmarReserva() {
    const dia = diaSelecionadoValido;
    if (!dia) return;

    CustomAlert({
      title: `Confirmação de Reserva ${espaco.esp}`,
      message: detalhesReserva?.codusu ?
        `Deseja Prosseguir com a Reserva Para o Dia ${format(dia, 'dd/MM/yyyy', { locale: ptBR })} para o Associado ${detalhesReserva.usu}?` :
        `Deseja Prosseguir com a Reserva Para o Dia ${format(dia, 'dd/MM/yyyy', { locale: ptBR })} no Valor de ${formatCurrency(valoresReserva(dia))}?`,
      onConfirm: async () => await postCheckout(dia),
      onCancel: () => { },
      exibirTermo: true
    })
  }

  useEffect(() => {
    getData();
  }, []);

  const diaSelecionadoValido = diaSelecionado && statusDoDia(diaSelecionado) === 'disponivel' ? diaSelecionado : undefined;
  const podeVoltarMes = isAfter(primeiroDiaDoMesAtual, startOfMonth(hoje));
  const podeAvancarMes = add(primeiroDiaDoMesAtual, { months: 1 }) < dataLimiteParaReserva;
  const textoBotaoConfirmar = detalhesReserva?.codusu ? 'Confirmar reserva' : 'Confirmar e pagar';

  const itensResumo = [
    { label: 'Espaço', valor: espaco.esp },
    { label: 'Tipo de reserva', valor: tipoDeReserva.des },
    { label: 'Data', valor: diaSelecionadoValido ? format(diaSelecionadoValido, "EEE, dd 'de' MMM 'de' yyyy", { locale: ptBR }) : undefined },
    ...(detalhesReserva?.usu ? [{ label: 'Associado', valor: detalhesReserva.usu }] : []),
  ];
  const destaqueResumo = {
    label: 'Valor',
    valor: diaSelecionadoValido ? formatCurrency(valoresReserva(diaSelecionadoValido)) : undefined
  };

  return (
    <div className='w-full pb-[calc(11rem+env(safe-area-inset-bottom))] md:pb-0'>
      <header className='mb-6 flex items-start gap-3'>
        {onVoltar && (
          <button
            type='button'
            onClick={onVoltar}
            aria-label='Voltar para a escolha do espaço'
            className='flex h-10 w-10 flex-none items-center justify-center rounded-full border border-zinc-200 text-zinc-700 hover:bg-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900'
          >
            <AiIcons.AiOutlineArrowLeft className='h-5 w-5' aria-hidden='true' />
          </button>
        )}
        <div className='flex-1'>
          <h1 className='text-2xl font-semibold text-zinc-900 md:text-3xl'>{espaco.esp}</h1>
          <div className='mt-1 flex flex-wrap items-center gap-2 text-sm text-zinc-500'>
            <span>{tipoDeReserva.des}</span>
            {espaco.tipesp.prares !== 0 && (
              <span className='rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-medium text-zinc-600'>
                Reservas até {format(dataLimiteParaReserva, 'dd/MM/yyyy', { locale: ptBR })}
              </span>
            )}
          </div>
        </div>
      </header>

      <div className='grid gap-6 md:grid-cols-[1fr_320px] md:items-start'>
        <section className='rounded-2xl border border-zinc-200 bg-white p-3 shadow-card sm:p-5'>
          <div className='mb-4 flex items-center'>
            <h2 className='flex-auto text-lg font-semibold capitalize text-zinc-900'>
              {format(primeiroDiaDoMesAtual, 'MMMM yyyy', { locale: ptBR })}
            </h2>
            <button
              type='button'
              onClick={mesAnterior}
              disabled={!podeVoltarMes}
              aria-label='Mês anterior'
              className='flex h-10 w-10 items-center justify-center rounded-full text-zinc-700 hover:bg-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 disabled:text-zinc-300 disabled:hover:bg-transparent'
            >
              <AiIcons.AiOutlineLeft className='w-5 h-5' aria-hidden='true' />
            </button>
            <button
              type='button'
              onClick={proximoMes}
              disabled={!podeAvancarMes}
              aria-label='Próximo mês'
              className='ml-1 flex h-10 w-10 items-center justify-center rounded-full text-zinc-700 hover:bg-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 disabled:text-zinc-300 disabled:hover:bg-transparent'
            >
              <AiIcons.AiOutlineRight className='w-5 h-5' aria-hidden='true' />
            </button>
          </div>

          <div className='grid grid-cols-7 gap-1 text-center text-[11px] font-medium uppercase tracking-wide text-zinc-400 sm:text-xs'>
            {diasDaSemana.map(diaDaSemana => (
              <div key={diaDaSemana} className='py-1'>{diaDaSemana}</div>
            ))}
          </div>

          <div className='mt-1 grid grid-cols-7 gap-1'>
            {dias.map((dia, index) => {
              const status = statusDoDia(dia);
              const selecionado = !!diaSelecionadoValido && isEqual(dia, diaSelecionadoValido);
              const valor = status === 'disponivel' ? valoresReserva(dia) : 0;

              return (
                <div
                  key={dia.toString()}
                  className={classNames(index === 0 && colStartClasses[getDay(dia)])}
                >
                  <button
                    type='button'
                    onClick={() => handleDiaButtonClick(dia)}
                    disabled={status !== 'disponivel'}
                    aria-pressed={selecionado}
                    aria-label={`${format(dia, "d 'de' MMMM", { locale: ptBR })}, ${status === 'disponivel' ? formatCurrency(valor) : ''} ${rotulosStatus[status]}`}
                    className={classNames(
                      'relative flex h-14 w-full flex-col items-center justify-center rounded-xl text-sm transition-colors md:h-16',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-1',
                      status === 'disponivel' && !selecionado && 'border border-zinc-200 bg-white font-semibold text-zinc-900 hover:border-zinc-900 hover:bg-zinc-50',
                      selecionado && 'border border-zinc-900 bg-zinc-900 font-semibold text-white shadow-card',
                      status === 'reservado' && 'bg-zinc-100 text-zinc-400 cursor-not-allowed',
                      (status === 'indisponivel' || status === 'foraDoPrazo') && 'text-zinc-300 cursor-not-allowed',
                      status === 'passado' && 'text-zinc-300 line-through cursor-not-allowed',
                      isToday(dia) && !selecionado && 'ring-1 ring-inset ring-zinc-900'
                    )}
                  >
                    <time dateTime={format(dia, 'yyyy-MM-dd')}>
                      {format(dia, 'dd')}
                    </time>
                    {status === 'disponivel' && (
                      <span className={`mt-0.5 text-[10px] font-medium leading-none md:text-xs ${selecionado ? 'text-zinc-300' : 'text-emerald-700'}`}>
                        <span className='md:hidden'>{precoCurto(valor)}</span>
                        <span className='hidden md:inline'>{formatCurrency(valor)}</span>
                      </span>
                    )}
                    {status === 'reservado' && (
                      <span className='mt-0.5 text-[9px] font-medium leading-none md:text-[10px]'>Reservado</span>
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          <ul className='mt-5 flex flex-wrap gap-x-4 gap-y-2 border-t border-zinc-100 pt-4 text-xs text-zinc-500'>
            <li className='flex items-center gap-1.5'><span className='h-3 w-3 rounded border border-zinc-300 bg-white' />Disponível</li>
            <li className='flex items-center gap-1.5'><span className='h-3 w-3 rounded bg-zinc-900' />Selecionado</li>
            <li className='flex items-center gap-1.5'><span className='h-3 w-3 rounded bg-zinc-100' />Reservado</li>
            <li className='flex items-center gap-1.5'><span className='h-3 w-3 rounded ring-1 ring-inset ring-zinc-900' />Hoje</li>
            <li className='flex items-center gap-1.5'><span className='text-zinc-300'>00</span>Indisponível</li>
          </ul>
        </section>

        <aside className='hidden md:sticky md:top-20 md:block'>
          <div className='rounded-2xl border border-zinc-200 bg-white p-5 shadow-card'>
            <ResumoReserva itens={itensResumo} destaque={destaqueResumo} />
            <button
              type='button'
              onClick={confirmarReserva}
              disabled={!diaSelecionadoValido}
              className='mt-5 flex h-12 w-full items-center justify-center rounded-xl bg-zinc-900 px-5 font-semibold text-white transition-colors hover:bg-zinc-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-zinc-200 disabled:text-zinc-400'
            >
              {textoBotaoConfirmar}
            </button>
            {!diaSelecionadoValido && (
              <p className='mt-2 text-center text-xs text-zinc-500'>Selecione uma data disponível no calendário</p>
            )}
          </div>
        </aside>
      </div>

      <div className='fixed inset-x-0 bottom-0 z-40 border-t border-zinc-200 bg-white/95 px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur md:hidden'>
        <div className='mb-3 flex items-center justify-between gap-3'>
          <div className='min-w-0'>
            <p className='text-xs text-zinc-500'>{diaSelecionadoValido ? 'Data selecionada' : 'Nenhuma data selecionada'}</p>
            <p className='truncate font-semibold capitalize text-zinc-900'>
              {diaSelecionadoValido ? format(diaSelecionadoValido, "EEE, dd 'de' MMM", { locale: ptBR }) : 'Toque em um dia disponível'}
            </p>
          </div>
          {diaSelecionadoValido && (
            <p className='flex-none text-xl font-semibold text-zinc-900'>{formatCurrency(valoresReserva(diaSelecionadoValido))}</p>
          )}
        </div>
        <button
          type='button'
          onClick={confirmarReserva}
          disabled={!diaSelecionadoValido}
          className='flex h-12 w-full items-center justify-center rounded-xl bg-zinc-900 px-5 font-semibold text-white transition-colors hover:bg-zinc-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-zinc-200 disabled:text-zinc-400'
        >
          {textoBotaoConfirmar}
        </button>
      </div>
    </div>
  )
}

type StatusDia = 'disponivel' | 'reservado' | 'indisponivel' | 'foraDoPrazo' | 'passado';

const rotulosStatus: Record<StatusDia, string> = {
  disponivel: 'disponível',
  reservado: 'reservado',
  indisponivel: 'indisponível',
  foraDoPrazo: 'fora do prazo de reserva',
  passado: 'data passada',
};

const diasDaSemana = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function precoCurto(valor: number) {
  return formatCurrency(valor).replace(/R\$\s?/, 'R$').replace(/,00$/, '');
}

let colStartClasses = [
  '',
  'col-start-2',
  'col-start-3',
  'col-start-4',
  'col-start-5',
  'col-start-6',
  'col-start-7',
]
